import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

type TranslationObject = Record<string, any>;

export const useTranslation = () => {
  const { currentLanguage } = useLanguage();
  const [translations, setTranslations] = useState<TranslationObject>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadTranslations = async () => {
      setIsLoading(true);
      try {
        // Dynamically import the translation file for the current language
        const translationModule = await import(`../../i18n/${currentLanguage.code}.json`);
        setTranslations(translationModule.default || translationModule);
      } catch (error) {
        console.error(`Failed to load translations for ${currentLanguage.code}:`, error);
        // Fallback to English if the current language fails
        try {
          const fallbackModule = await import(`../../i18n/en.json`);
          setTranslations(fallbackModule.default || fallbackModule);
        } catch (fallbackError) {
          console.error('Failed to load fallback English translations:', fallbackError);
          setTranslations({});
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadTranslations();
  }, [currentLanguage.code]);

  // Translation function with dot notation support
  const t = (key: string, fallback?: string): string => {
    const keys = key.split('.');
    let value: any = translations;

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Return fallback or the key itself if translation not found
        return fallback || key;
      }
    }

    return typeof value === 'string' ? value : fallback || key;
  };

  return { t, isLoading, currentLanguage };
}; 